import { ChangeEvent } from 'react';

export function generateUUID() {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
}

export function formatCurrency(num: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(num);
}

export function formateDate() {
  const date = new Date();
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
}

export function capitalize(str: string) {
  return str.substring(0, 1).toUpperCase() + str.substring(1);
}

export function convertDate(dateString: string) {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().substr(-2);
  return `${day}/${month}/${year}`;
}

export function truncate(str: string, limit: number) {
  const words = str.split(' ');
  return words.reduce((cur, next) => {
    if (cur.slice(-3) === '...') return cur;

    const res = `${cur} ${next}`;
    const strLen = res.length;
    if (strLen <= limit) return res;

    const diffLen = strLen - limit;
    if (next.length - diffLen >= Math.round(next.length / 2)) return res;

    return cur + '...';
  });
}

export function getDateTime(inputString: string) {
  const date = new Date(inputString);
  const formattedTime = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `${convertDate(inputString)} at ${formattedTime}`;
}

export function truncateString(str: string, maxLength: number) {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength) + '...';
}

export function acronym(longString: string, joiner = '') {
  return longString
    .toUpperCase()
    .split(' ')
    .reduce((prev, cur) => prev + joiner + cur[0], '')
    .trim();
}

export function formatExpiry(inputString: string) {
  const cleanedValue = inputString.replace(/\//g, '');

  let formattedValue = '';

  for (let i = 0; i < cleanedValue.length; i += 2) {
    formattedValue += cleanedValue.slice(i, i + 2);
    if (i + 2 < cleanedValue.length) {
      formattedValue += '/';
    }
  }

  return formattedValue;
}

export function isValidDate(dateString: string) {
  // Check if the date string is in YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    return false;
  }

  // Convert the date string to a Date object
  const dateParts = dateString.split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);

  // Check if the Date object is valid
  const dateObj = new Date(year, month - 1, day);
  if (
    isNaN(dateObj.getTime()) ||
    dateObj.getFullYear() !== year ||
    dateObj.getMonth() !== month - 1 ||
    dateObj.getDate() !== day
  ) {
    return false;
  }

  // Calculate the date range (200 years ago to 200 years in the future)
  const today = new Date();
  const twoHundredYearsAgo = new Date(today);
  twoHundredYearsAgo.setFullYear(today.getFullYear() - 200);
  const twoHundredYearsLater = new Date(today);
  twoHundredYearsLater.setFullYear(today.getFullYear() + 200);

  return dateObj >= twoHundredYearsAgo && dateObj <= twoHundredYearsLater;
}

export function isAfterToday(dateString: string) {
  // Check if the date string is in required format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    return false;
  }

  // Convert the date string to a Date object
  const dateParts = dateString.split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);

  // Check if the Date object is valid
  const dateObj = new Date(year, month - 1, day);
  if (
    isNaN(dateObj.getTime()) ||
    dateObj.getFullYear() !== year ||
    dateObj.getMonth() !== month - 1 ||
    dateObj.getDate() !== day
  ) {
    return false;
  }

  const today = new Date();
  return dateObj > today;
}

export function sortCode(input: string) {
  const cleanedValue = input.replace(/-/g, ''); // Remove existing hyphens if any

  let formattedValue = '';

  for (let i = 0; i < cleanedValue.length; i += 2) {
    formattedValue += cleanedValue.slice(i, i + 2);
    if (i + 2 < cleanedValue.length) {
      formattedValue += '-';
    }
  }

  return formattedValue;
}

export function formatStripeError(inputError: string) {
  return inputError.replace('state', 'county');
}

export const patternSortCode = (e: ChangeEvent<HTMLInputElement>) => {
  const cleanedValue = e.target.value.replace(/[^\d]/g, '');

  let formattedValue = '';
  for (let i = 0; i < cleanedValue.length; i += 2) {
    formattedValue += cleanedValue.slice(i, i + 2);
    if (i + 2 < cleanedValue.length) {
      formattedValue += '-';
    }
  }

  e.target.value = formattedValue;
};

export const formatDescription = (description: string): string[] => {
  if (description == null || description === '' || description === undefined)
    return [];
  const firstColonIndex = description.indexOf(':');
  if (firstColonIndex === -1) return [description];

  const formattedParts: string[] = [];
  let lastIndex = 0;

  const regex = /(\w+)(?=\s*:)/g;
  let match;

  while ((match = regex.exec(description)) !== null) {
    const offset = match.index;

    if (offset > firstColonIndex) {
      formattedParts.push(description.slice(lastIndex, offset).trim());
      lastIndex = offset;
    }
  }

  if (lastIndex < description.length) {
    formattedParts.push(description.slice(lastIndex).trim());
  }

  return formattedParts;
};
